import React from 'react';
import { Navigate, type RouteObject, useLocation, useRoutes } from 'react-router';
import AdminRoute from 'web/components/AdminRoute';
import { BackButtonProvider } from 'web/components/BackButtonContext';
import CookieBanner from 'web/components/CookieBanner';
import ErrorBoundary from 'web/components/ErrorBoundary';
import { shouldPreloadUserDataOnPath } from 'web/components/FirebaseContext/firestorePreload';
import { GeoIpProvider } from 'web/components/GeoIpContext';
import PageLoadingFallback from 'web/components/PageLoadingFallback';
import PageTracker from 'web/components/PageTracker';
import PrivateRoute from 'web/components/PrivateRoute';
import Rewrite from 'web/components/Rewrite';
import SubstituteUserWarning from 'web/components/SubstituteUserWarning';
import { TrackingProvider } from 'web/components/TrackingContext';
import UserProvider from 'web/components/UserProvider';
import UserTracker from 'web/components/UserTracker';
import 'web/styles/GlobalStyle';

const fallback = <PageLoadingFallback />;
const dashboardRoutePrefixes = ['/dashboard', '/home', '/settings'] as const;
const FullPageReload = () => {
  React.useEffect(() => {
    window.location.assign(window.location.href);
  }, []);

  return <PageLoadingFallback />;
};

const DashboardCatchAllRoute = () => {
  const { pathname } = useLocation();

  if (dashboardRoutePrefixes.some((prefix) => pathname.startsWith(prefix))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <FullPageReload />;
};

const Login = React.lazy(() => import('./Login'));
const Logout = React.lazy(() => import('./Logout'));
const Signup = React.lazy(() => import('./Signup'));
const EmailAction = React.lazy(() => import('./EmailAction'));
const ResetPassword = React.lazy(() => import('./ResetPassword'));
const Welcome = React.lazy(() => import('./Welcome'));
const Onboarding = React.lazy(() => import('./Onboarding'));
const Admin = React.lazy(() => import('./Admin'));
const AppSumo = React.lazy(() => import('./AppSumo'));
const PaymentAction = React.lazy(() => import('./PaymentAction'));
const Dashboard = React.lazy(() => import('./Dashboard/Dashboard'));
const StripeOAuth = React.lazy(() => import('./StripeOAuth'));
const PayPalReturn = React.lazy(() => import('./PayPalReturn'));
const ZoomOAuth = React.lazy(() => import('./ZoomOAuth'));
const OAuthAction = React.lazy(() => import('./OAuthAction'));

export const routes: RouteObject[] = [
  { path: '/login', element: <Login /> },
  { path: '/logout', element: <Logout /> },
  {
    path: '/get-started',
    element: (
      <GeoIpProvider>
        <Signup />
      </GeoIpProvider>
    ),
  },
  { path: '/appsumo/*', element: <AppSumo /> },
  { path: '/email-action', element: <EmailAction /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/welcome/*', element: <Welcome /> },
  { path: '/onboarding/*', element: <Onboarding /> },
  {
    path: '/admin/*',
    element: (
      <AdminRoute>
        <Admin />
      </AdminRoute>
    ),
  },
  { path: '/payment-action', element: <PaymentAction /> },
  {
    path: '/oauth/user-action',
    element: (
      <PrivateRoute waitForData placeholder={<PageLoadingFallback />}>
        <OAuthAction />
      </PrivateRoute>
    ),
  },
  {
    path: '/dashboard/payments/stripe-oauth',
    element: (
      <PrivateRoute waitForData placeholder={<PageLoadingFallback />}>
        <StripeOAuth />
      </PrivateRoute>
    ),
  },
  {
    path: '/dashboard/payments/paypal-return',
    element: (
      <PrivateRoute waitForData placeholder={<PageLoadingFallback />}>
        <PayPalReturn continueTo="/dashboard/payments/accounts" />
      </PrivateRoute>
    ),
  },
  {
    path: '/settings/scheduling/zoom-oauth',
    element: (
      <PrivateRoute waitForData placeholder={<PageLoadingFallback />}>
        <ZoomOAuth />
      </PrivateRoute>
    ),
  },
  {
    path: '/dashboard/*',
    element: (
      <PrivateRoute waitForData placeholder={<PageLoadingFallback />}>
        <Dashboard />
      </PrivateRoute>
    ),
  },
  {
    path: '/home/*',
    element: <Rewrite to={(from) => `/dashboard${from}`} />,
  },
  {
    path: '/settings/*',
    element: <Rewrite to={(from) => `/dashboard${from.replace('/settings', '')}`} />,
  },
  {
    path: '*',
    element: <DashboardCatchAllRoute />,
  },
];

const DashboardApp = () => {
  const element = useRoutes(routes);
  const { pathname } = useLocation();
  return (
    <CookieBanner>
      <UserProvider preloadFirestore={shouldPreloadUserDataOnPath(pathname)}>
        <TrackingProvider>
          <BackButtonProvider>
            <PageTracker />
            <UserTracker />
            <ErrorBoundary>
              <SubstituteUserWarning />
              <React.Suspense fallback={fallback}>{element}</React.Suspense>
            </ErrorBoundary>
          </BackButtonProvider>
        </TrackingProvider>
      </UserProvider>
    </CookieBanner>
  );
};

export default DashboardApp;
